import React, { useEffect, useRef, useState } from 'react';

export default function HeroCarousel({
  slides = [],
  interval = 3500,        // 自動播放間隔（毫秒）
  aspectRatio = '16/9',
  title = '最新消息',
  panelBg = '#f0dfd4',
}) {
  // ------- hooks 先宣告 -------
  const [vIdx, setVIdx]   = useState(1);   // 含 clone 的索引
  const [anim, setAnim]   = useState(true);
  const [wrapW, setWrapW] = useState(0);
  const holdRef   = useRef(false);
  const startXRef = useRef(0);

  // 進行中節流
  const movingRef = useRef(false);
  const timerRef  = useRef(null);
  const TRANS_MS  = 450;

  // 預載快取
  const loadedRef = useRef(new Set());
  const [, force] = useState(0);

  const L  = slides.length;
  const ex = L > 0 ? [slides[L - 1], ...slides, slides[0]] : [];

  // ------- 量測寬度（像素位移更穩） -------
  const wrapRef = useRef(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((ents) => {
      setWrapW(Math.round(ents[0].contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ------- 圖片預載 -------
  const loadImage = (src) =>
    new Promise((resolve) => {
      if (!src || loadedRef.current.has(src)) return resolve();
      const img = new Image();
      img.onload = () => { loadedRef.current.add(src); force(x => x + 1); resolve(); };
      img.onerror = () => resolve();
      img.src = src;
      if (img.decode) img.decode().catch(() => {});
    });

  useEffect(() => {
    let alive = true;
    (async () => {
      for (const s of slides) {
        if (!alive) break;
        await loadImage(s?.src);
      }
    })();
    return () => { alive = false; };
  }, [slides]);

  // 切換時預載鄰近兩張
  useEffect(() => {
    if (L <= 0) return;
    const cur  = ((vIdx - 1) % L + L) % L;
    const prev = (cur - 1 + L) % L;
    const next = (cur + 1) % L;
    [cur, prev, next].forEach(i => loadImage(slides[i]?.src));
  }, [vIdx, L, slides]);

  // ------- 自動播放（永遠開著；遇到 hold/moving 就跳過一次） -------
  useEffect(() => {
    const t = setInterval(() => {
      if (L > 1 && !holdRef.current && !movingRef.current) go(1);
    }, interval);
    return () => clearInterval(t);
  }, [L, interval]); // 不依賴 vIdx，避免每次切換都重設

  const onTransitionEnd = () => {
    movingRef.current = false;
    if (L <= 1) return;
    if (vIdx === L + 1) { // 尾端 clone → 回到第 1 張
      setAnim(false); setVIdx(1);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    }
    if (vIdx === 0) {     // 前端 clone → 回到最後一張
      setAnim(false); setVIdx(L);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    }
  };

  const go   = (dir) => {
    if (movingRef.current) return;
    movingRef.current = true;
    clearTimeout(timerRef.current);
    setVIdx(i => i + dir);
    timerRef.current = setTimeout(() => { movingRef.current = false; }, TRANS_MS + 60);
  };
  const prev = () => go(-1);
  const next = () => go(1);

  // 觸控/滑鼠拖曳
  const onDown = (e) => {
    holdRef.current = true;
    startXRef.current = e.touches ? e.touches[0].clientX : e.clientX;
  };
  const onUp = (e) => {
    const x  = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const dx = x - startXRef.current;
    if (dx > 40) prev();
    if (dx < -40) next();
    setTimeout(() => (holdRef.current = false), 120);
  };

  const activeDot = ((vIdx - 1) % L + L) % L; // 0..L-1

  const trackStyle = {
    ...styles.track,
    width: wrapW * ex.length,
    transform: `translateX(-${vIdx * wrapW}px)`,
    transition: anim ? `transform ${TRANS_MS}ms ease` : 'none',
  };
  const slideStyle = { ...styles.slide, width: wrapW };

  return (
    <div style={{ ...styles.card, background: panelBg }}>
      <div style={{ ...styles.header, background: panelBg }}>
        <span style={styles.headerText}>{title}</span>
      </div>

      <div
        ref={wrapRef}
        style={{ ...styles.wrap, aspectRatio }}
        onTouchStart={onDown} onTouchEnd={onUp} onTouchCancel={() => (holdRef.current = false)}
        onMouseDown={onDown}  onMouseUp={onUp}
        onMouseLeave={() => (holdRef.current = false)}
      >
        {L === 0 ? (
          // 沒資料時：顯示黑底佔位
          <div style={styles.empty}>
            <span style={styles.emptyText}>暫無最新消息</span>
          </div>
        ) : (
          <>
            <div style={trackStyle} onTransitionEnd={onTransitionEnd}>
              {ex.map((it, i) => {
                const src   = it?.src || '';
                const ready = loadedRef.current.has(src);
                return (
                  <div key={i} style={slideStyle}>
                    <div style={styles.imgBox}>
                      {!ready && <div style={styles.placeholder} />}
                      {src && (
                        <img
                          src={src}
                          alt={it.alt || ''}
                          style={{ ...styles.img, opacity: ready ? 1 : 0 }}
                          draggable={false}
                          loading="eager"
                          fetchpriority={i === vIdx ? 'high' : 'low'}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {L > 1 && (
              <div style={styles.dots}>
                {slides.map((_, i) => (
                  <button
                    key={i}
                    style={{ ...styles.dot, opacity: i === activeDot ? 1 : 0.35 }}
                    onClick={() => go(i + 1 - vIdx)}
                    aria-label={`go ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  card:   { borderRadius: 18, overflow: 'hidden', boxShadow: '0 6px 18px rgba(0,0,0,.18)' },
  header: { padding: '10px 14px', borderBottom: '1px solid rgba(0,0,0,.06)' },
  headerText: { color: '#111', fontSize: 16, fontWeight: 800, letterSpacing: '.08em' },

  wrap:  { position: 'relative', width: '100%', background: '#000', overflow: 'hidden' }, // 圖片區黑底
  track: { display: 'flex', height: '100%', willChange: 'transform' },
  slide: { flex: '0 0 auto', height: '100%' },

  imgBox: { width: '100%', height: '100%', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  img:    { width: '100%', height: '100%', objectFit: 'contain', transition: 'opacity .2s linear' },
  placeholder: { width: '100%', height: '100%', background: 'rgba(255,255,255,.05)' },

  dots: { position:'absolute', left:0, right:0, bottom:8, display:'flex', justifyContent:'center', gap:4, zIndex:2 },
  dot:  { width:5, height:6, borderRadius:6, border:'none', background:'#fff', cursor:'pointer' },

  // 空狀態
  empty: { width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'#000' },
  emptyText: { color:'#fff', opacity:.7, fontSize:14, letterSpacing:'.1em' },
};
